import React, { Suspense, useMemo, type ReactNode } from 'react';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { MemoryRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarProvider } from '@/contexts/snackContext';
import HelpSupportProvider from '@/contexts/helpSupportContext';
import HelpSupportModal from '@/components/help-support-modal/HelpSupportModal';
import { FeatureFlagProvider } from '@/components/providers/FeatureFlagProvider';
import { APP_GLOBAL_STYLE_OVERRIDES, LightTheme } from '@/constants/theme';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import { widgetStore } from './widget-store';
import { Box, CircularProgress } from '@mui/material';
import {
  createEmbeddableBaselineStyles,
  createEmbeddableWidgetTheme,
  EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY,
  isEmbeddableWidgetBundle,
} from './embeddableWidgetPortalUtils';
import {
  EmbeddableWidgetShell,
  WidgetPortalProvider,
} from './embeddableWidgetPortal';
import { useUpdateFeatureFlagContext } from '@/hooks/useFeatureFlags';
import { UserContext, defaultUserContextValue } from '@/contexts/userContext';
import type { WARootState } from '@/store/store';

interface WidgetProvidersProps {
  widgetKey?: string;
  /** Host mount element (#widget-container). Required for scoped overlays in the embed bundle. */
  portalContainer?: HTMLElement;
  children: ReactNode;
}

const LoadingFallback = () => (
  <Box
    display='flex'
    justifyContent='center'
    alignItems='center'
    minHeight={120}
  >
    <CircularProgress />
  </Box>
);

const FeatureFlagContextUpdater = () => {
  useUpdateFeatureFlagContext();
  return null;
};

/**
 * Exposes the widget's SSO-hydrated user to `UserContext` consumers such as
 * `useTabs`, which returns no tabs while `userInfo` is null (hiding the
 * Account Details flyout tabs). `UserProvider` is deliberately not reused: it
 * navigates on role change and issues auth/appView queries that don't apply
 * to an embedded widget.
 */
const WidgetUserContextBridge = ({ children }: { children: ReactNode }) => {
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);
  const { isAuthenticated, currentRole } = useSelector(
    (state: WARootState) => state.auth,
  );

  const value = useMemo(
    () => ({
      ...defaultUserContextValue,
      userInfo,
      currentRole: currentRole ?? '',
      isAuthenticated,
      isLoggedIn: isAuthenticated,
    }),
    [userInfo, currentRole, isAuthenticated],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * Minimal provider tree for rendering standalone widgets outside the main app.
 *
 * Includes Redux (with the full store so RTK Query works), MUI theme,
 * date-picker localisation, snackbar support, feature flags, Suspense, and an error boundary.
 *
 * MemoryRouter is included because several widgets call `useNavigate`
 * internally. Navigation calls are effectively no-ops in the widget context
 * but the hook requires a Router ancestor to avoid throwing.
 */
export const WidgetProviders: React.FC<WidgetProvidersProps> = ({
  widgetKey,
  portalContainer,
  children,
}) => {
  const theme = useMemo(
    () =>
      isEmbeddableWidgetBundle && portalContainer
        ? createEmbeddableWidgetTheme(portalContainer)
        : LightTheme,
    [portalContainer],
  );

  const useEmbeddablePickerDialogMode =
    isEmbeddableWidgetBundle && portalContainer != null;

  // `CssBaseline` styles `html`/`body`/`*`, which leaks the widget's font,
  // font size and background onto the embedding page (see Salesforce). Embedded
  // builds get the same reset scoped to their mount node instead.
  const baseline = useEmbeddablePickerDialogMode ? (
    <GlobalStyles
      styles={(muiTheme) => [
        APP_GLOBAL_STYLE_OVERRIDES,
        createEmbeddableBaselineStyles(muiTheme),
      ]}
    />
  ) : (
    <CssBaseline />
  );

  const providers = (
    <ThemeProvider theme={theme}>
      {baseline}
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        desktopModeMediaQuery={
          useEmbeddablePickerDialogMode
            ? EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY
            : undefined
        }
      >
        <SnackbarProvider>
          <HelpSupportProvider>
            <Suspense fallback={<LoadingFallback />}>
              <WidgetErrorBoundary widgetKey={widgetKey}>
                <WidgetUserContextBridge>
                  {/*
                    Target for "Contact support" in the connection-error modal. The
                    app mounts this in `Layout`, which widgets do not use, so without
                    it the button would set context state nothing renders.
                  */}
                  <HelpSupportModal />
                  {children}
                </WidgetUserContextBridge>
              </WidgetErrorBoundary>
            </Suspense>
          </HelpSupportProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );

  return (
    <ReduxProvider store={widgetStore}>
      <FeatureFlagProvider>
        <FeatureFlagContextUpdater />
        <MemoryRouter>
          {isEmbeddableWidgetBundle && portalContainer ? (
            <WidgetPortalProvider container={portalContainer}>
              <EmbeddableWidgetShell container={portalContainer}>
                {providers}
              </EmbeddableWidgetShell>
            </WidgetPortalProvider>
          ) : (
            providers
          )}
        </MemoryRouter>
      </FeatureFlagProvider>
    </ReduxProvider>
  );
};
